import { contentBlockAt, MAX_STREAM_BLOCKS, preserveBlockSlots } from "./content-blocks";
import { ChatBlockReducers, type ChatBlockKind } from "../model/block-reducers";
import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { $$ } from "@tento-ui/fastdom";
import { DateTime } from "@tento-chrono";
import {
  type ChatBlockPlugin,
  ChatPluginManager,
  CodeBlockPlugin,
  ErrorBlockPlugin,
  InterruptedBlockPlugin,
  LoadingBlockPlugin,
  QuietGapBlockPlugin,
  MarkdownBlockPlugin,
  TextBlockPlugin,
  UnknownBlockPlugin,
  TerminalBlockPlugin,
  ToolCallBlockPlugin,
} from "./chat-block-plugin";
import { ChatBubble } from "./chat-bubble";
import type { ChatTheme } from "./chat-theme";
import type { ChatTurn } from "./chat-turn";
import {
  ChatContentBlock,
  ChatMessage,
  StreamDelta,
} from "./chat-types";
import { Tracing } from "@tento-core/tracing";
import { ScrollEngine } from "@tento-scroll/engine/scroll-engine";
import { ScrollVertical } from "@tento-scroll/vertical/scroll-vertical";
import "@tento-scroll/vertical/scroll-vertical";
import { Renderers } from "../render/prose";
import {
  judgedDistanceFromBottomPx,
  pinnedAfterScroll,
  repinsAfterHeightFlush,
  unflushedEndShiftPx,
} from "./auto-scroll";
import type { ChatRenderers } from "../render/prose";

const CONSERVATIVE_CELL_HEIGHT = 24;
/** addStyle slot for the active theme's sheet — one theme, one slot. */
const THEME_STYLE_ID = "chat-theme";

type ThreadAnchor = { id: string; index: number; offset: number };
type GeometryRecovery = { anchor?: ThreadAnchor; inputRevision: number; started: boolean };

@component({ name: "std-chat-thread" })
export class ChatThread extends LonaWebComponent {
  private $scroll: ScrollVertical<ChatTurn> = this.hydrate("scroll");

  get $scrollViewport(): HTMLElement {
    return this.$scroll.$viewport;
  }

  get scrollEngine(): ScrollEngine<ChatTurn> {
    return this.$scroll.scrollEngine;
  }

  private messages: ChatMessage[] = [];
  private measuredHeights: Map<string, number> = new Map();
  private pluginManager: ChatPluginManager;
  private blockReducers = new ChatBlockReducers();
  private autoScrollEnabled: boolean = true;
  private userScrolling: boolean = false;
  /** The one message whose next bind is an ARRIVAL — see onBind. */
  private enteringId: Option<string> = null;
  /** The one live entering animation: its bubble and its timer, so a pooled
   * bubble recycled and re-bound inside the 600ms window cannot have the OLD
   * message's timer strip the attribute off the NEW message's first paint. */
  private entering: Option<{ $bubble: HTMLElement; timer: number }> = null;

  private clearEntering($bubble: Option<HTMLElement>): void {
    if (this.entering == null) return;
    if ($bubble != null && this.entering.$bubble !== $bubble) return;
    clearTimeout(this.entering.timer);
    this.entering.$bubble.removeAttribute("entering");
    this.entering = null;
  }
  private scrollSettleTimeout: Option<number> = null;
  private lastScrollTop: number = 0;
  private bindReady = false;
  private disposed = false;
  private bindGeneration = 0;
  private anchorFrame: number | null = null;
  private anchorInputRevision = 0;
  private anchorInputController: AbortController | null = null;
  private geometryObserver: ResizeObserver | null = null;
  private geometryLayoutFrame: number | null = null;
  private lastValidAnchor: ThreadAnchor | undefined;
  private lastValidGeometryRevision = -1;
  private geometryRecovery: GeometryRecovery | undefined;
  private geometryPlacement: { top: number; inputRevision: number } | undefined;
  private heightsDirty: boolean = false;
  private dirtyMinIndex: number = Number.MAX_SAFE_INTEGER;
  /** How far the end of the list has moved in height reports the engine has
   * not laid out yet: the sum of {@link unflushedEndShiftPx} since the last
   * flush. A pending shrink brings a scroll event's distance to the end
   * closer; a pending growth does not push it away. */
  private unflushedEndShiftPx: number = 0;
  showBoundaries: boolean = false;
  onMessageClick: Option<(messageId: string) => void> = null;
  /** Per-thread renderer override (the prose/code seam, `render/prose.ts`).
   * Empty means the page-wide `Renderers` registry answers. */
  private _renderers: Partial<ChatRenderers> = {};
  /**
   * Block types a HOST appends to a message as decoration between deltas —
   * the lona sidebar adds a sheet attachment, a cell-update note or a
   * proposal list after a `tool_use` — and that the thread must drop again
   * the moment new content or the tool's own blocks arrive, so a decoration
   * never outlives the turn it annotated. The library knows no product type;
   * the host names its own here.
   */
  transientBlockTypes: Set<string> = new Set();
  private _theme: Option<ChatTheme> = null;
  private pendingToolBlocks: Map<string, ChatContentBlock[]> = new Map();

  /**
   * The register this thread renders turns in. Null is the chat surface's
   * own answer — the bubble theme, with its role chrome. The thread's
   * scrolling, recycling, and streaming are indifferent to the choice,
   * which is what {@link ChatTurn} promises. Assignment is fire-and-forget
   * sugar for {@link setTheme}.
   */
  get theme(): Option<ChatTheme> {
    return this._theme;
  }

  set theme(theme: Option<ChatTheme>) {
    void this.setTheme(theme);
  }

  /**
   * Choose — or swap — the theme. Before the first bind this is free: the
   * factory is installed and the theme's sheet (if any) is adopted into
   * this shadow root. After cells exist a swap is a FULL RE-RENDER by
   * design: the engine's pool is register-shaped (a recycled bubble must
   * come back as a bubble) and the measured heights are register-shaped too
   * (the same message is 3 rows in a terminal and 120px in a bubble), so
   * both are discarded — the cells drained through
   * {@link ScrollVertical.resetCells} — and the thread rebinds from
   * `messages`, the one source of truth it already keeps. Two racing swaps
   * resolve last-writer-wins on `_theme`.
   */
  async setTheme(theme: Option<ChatTheme>): Promise<void> {
    if (this.disposed || this._theme === theme) return;
    const anchor = this.captureAnchor();
    this._theme = theme;
    // One total call, not withdraw-then-install. The manager keeps a theme's
    // grammar in a layer of its own, so this cannot reach the renderers the
    // constructor registered or the ones a host contributed — and there is no
    // second `plugins()` call whose answer has to agree with the first one's
    // for the swap to come out clean.
    this.pluginManager.installTheme(theme?.plugins() ?? []);
    if (theme?.$style) {
      this.addStyle(THEME_STYLE_ID, theme.$style);
    } else {
      this.removeStyle(THEME_STYLE_ID);
    }
    if (!this.bindReady) return;
    this.bindReady = false;
    this.measuredHeights.clear();
    this.unflushedEndShiftPx = 0;
    this.$scroll.resetCells(($turn) => $turn.recycle());
    await this.rebind(anchor);
  }

  /**
   * Contribute a content-block renderer. Registration is by `type` key,
   * last writer wins — which is also how a host overrides a stock block's
   * rendering. Register before binding messages that carry the type; a
   * block whose type has no plugin falls back to plain text.
   */
  registerPlugin(plugin: ChatBlockPlugin): void {
    this.pluginManager.registerHost(plugin);
  }

  /** Register canonical delta reduction independently of a theme renderer. */
  registerKind<K extends ChatContentBlock["type"]>(kind: ChatBlockKind<K>, renderer?: ChatBlockPlugin): void {
    if (renderer && renderer.type !== kind.type) throw new RangeError("Kind/renderer type mismatch");
    this.blockReducers.register(kind);
    if (renderer) this.pluginManager.registerHost(renderer);
  }

  /** Explicit retirement releases resources owned by otherwise plain DOM blocks. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.stopGeometryObservation();
    this.geometryRecovery = undefined;
    this.geometryPlacement = undefined;
    this.lastValidAnchor = undefined;
    this.bindGeneration++;
    if (this.anchorFrame != null) cancelAnimationFrame(this.anchorFrame);
    this.anchorFrame = null;
    this.anchorInputController?.abort();
    this.anchorInputController = null;
    this.bindReady = false;
    this.clearEntering(null);
    if (this.scrollSettleTimeout != null) clearTimeout(this.scrollSettleTimeout);
    this.scrollSettleTimeout = null;
    this.heightsDirty = false;
    this.dirtyMinIndex = Number.MAX_SAFE_INTEGER;
    this.$scroll.scrollEngine.dispose();
    this.$scroll.resetCells(($turn) => $turn.recycle());
    this.$scroll.teardown();
    this.pendingToolBlocks.clear();
    this.pluginManager.disclosure.clear();
    this.messages = [];
    this.measuredHeights.clear();
    this.onMessageClick = null;
  }

  /** Override the page-wide renderers for this thread only. Partial: a
   * thread may swap just prose, or just code. Takes effect on the next bind
   * of each block; call before binding messages, or rebind after. */
  setRenderers(renderers: Partial<ChatRenderers>): void {
    this._renderers = renderers;
  }

  private renderers(): ChatRenderers {
    return { ...Renderers.current(), ...this._renderers };
  }

  constructor() {
    super();

    this.pluginManager = new ChatPluginManager(new UnknownBlockPlugin());
    this.pluginManager.register(new TextBlockPlugin());
    this.pluginManager.register(new LoadingBlockPlugin());
    this.pluginManager.register(new QuietGapBlockPlugin());
    this.pluginManager.register(new MarkdownBlockPlugin(() => this.renderers()));
    this.pluginManager.register(new InterruptedBlockPlugin());
    this.pluginManager.register(new ErrorBlockPlugin());
    this.pluginManager.register(new CodeBlockPlugin(() => this.renderers()));
    this.pluginManager.register(new TerminalBlockPlugin());
    this.pluginManager.register(new ToolCallBlockPlugin());
    // Dialect plugins are NOT registered here. A grammar belongs to the theme
    // that speaks it: `setTheme` installs `theme.plugins()` and withdraws the
    // outgoing theme's, so a thread renders one look's grammar rather than
    // every look's at once, and a new look costs no edit to this file.
    // Product blocks (sheet attachments, cell updates, proposals) are NOT
    // registered here either: the lona sidebar registers its own through
    // `registerPlugin`, and the library names no product type.

    this.$scroll.initialize({
      onCreate: () => {
        const $turn = this.theme?.makeTurn() ?? (ChatBubble.make() as ChatTurn);
        $turn.onHeightMeasured = (index, height) =>
          this.registerHeight(index, height);
        $turn.onMessageClick = (id) => this.onMessageClick?.(id);
        return $turn;
      },
      onBind: ($bubble, index, _heightPx, resizeOnly) => {
        if (resizeOnly) return;
        const message = this.messages[index];
        if (!message) return;
        $bubble.onHeightMeasured = (idx, height) =>
          this.registerHeight(idx, height);
        $bubble.onMessageClick = (id) => this.onMessageClick?.(id);
        $bubble.bind(message, this.pluginManager, index);
        $bubble.toggleAttribute("debug-boundaries", this.showBoundaries);
        // An APPENDED message announces itself: its first bind carries a
        // transient `entering` attribute the look's sheet may animate
        // (fade, rise — the look decides, and owns the reduced-motion
        // guard). Keyed on the message id, not the index, and consumed on
        // first bind — a recycled cell scrolling back into view is old
        // content and must not re-announce.
        // Any rebind of the bubble that currently owns the entering timer
        // settles that animation first — this bind is new content.
        this.clearEntering($bubble);
        if (this.enteringId != null && message.id === this.enteringId) {
          this.enteringId = null;
          $bubble.setAttribute("entering", "");
          const timer = setTimeout(() => {
            if (this.entering?.$bubble === $bubble) {
              this.clearEntering($bubble);
            }
          }, 600);
          this.entering = { $bubble, timer };
        }
      },
      onRecycle: ($bubble) => {
        this.clearEntering($bubble);
        $bubble.recycle();
      },
      onScroll: (info) => {
        this.scheduleGeometryLayout();
        if (!this.hasUsableGeometry()) this.rememberGeometryLoss();
        // A lost box can clamp native scroll to zero, with its event delivered
        // after the box is back. Keep the logical position until placement is
        // restored, unless newer reader input has cancelled that recovery.
        if (this.geometryRecovery) return;
        // The correction's own unchanged-position notifications carry no new
        // reader intent and must not re-pin an anchor near the tail threshold.
        if (this.geometryPlacement?.inputRevision === this.anchorInputRevision &&
          this.geometryPlacement.top === info.top) return;
        this.geometryPlacement = undefined;
        // Judged against the nearer of the end the engine has laid out and
        // the end the thread has measured (see `judgedDistanceFromBottomPx`).
        const distanceFromBottom = judgedDistanceFromBottomPx({
          laidOutPx: info.scrollHeight - info.top - this.offsetHeight,
          unflushedShiftPx: this.unflushedEndShiftPx,
        });
        const pinned = pinnedAfterScroll({
          prevTop: this.lastScrollTop,
          top: info.top,
          distanceFromBottom,
          wasPinned: this.autoScrollEnabled,
        });
        if (pinned !== this.autoScrollEnabled) {
          // Every pin transition, with the scroll that caused it: the
          // playground's stuck report was only diagnosable from its flush
          // lines, which say THAT auto-scroll was off, not why.
          Tracing.log("chat/autoScroll", {
            enabled: pinned,
            top: Math.round(info.top),
            prevTop: Math.round(this.lastScrollTop),
            distanceFromBottom: Math.round(distanceFromBottom),
          });
        }
        this.lastScrollTop = info.top;
        this.autoScrollEnabled = pinned;
        this.userScrolling = true;
        if (this.scrollSettleTimeout) clearTimeout(this.scrollSettleTimeout);
        this.scrollSettleTimeout = setTimeout(() => {
          this.scrollSettleTimeout = null;
          this.userScrolling = false;
          if (this.autoScrollEnabled) this.scrollToBottom();
        }, 150);
      },
      options: {
        onRenderedIndiciesChanged: () => this.scheduleGeometryLayout(),
        onBatchComplete: () => this.scheduleGeometryLayout(),
        cellWidthForIndex: (index) => {
          const msg = this.messages[index];
          if (!msg) return null;
          return this.measuredHeights.get(msg.id) ?? null;
        },
      },
    });
  }

  connectedCallback(): void {
    super.connectedCallback();
    if (this.disposed) return;
    this.observeAnchorInput();
    this.geometryObserver = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width <= 0 || entry.contentRect.height <= 0) this.rememberGeometryLoss();
      this.onGeometryLayout();
      this.scheduleGeometryLayout();
    });
    this.geometryObserver.observe(this.$scrollViewport);
    this.onGeometryLayout();
  }

  disconnectedCallback(): void {
    this.rememberGeometryLoss();
    this.stopGeometryObservation();
    if (this.geometryRecovery) this.geometryRecovery.started = false;
    if (this.anchorFrame != null) cancelAnimationFrame(this.anchorFrame);
    this.anchorFrame = null;
    this.anchorInputController?.abort();
    this.anchorInputController = null;
    super.disconnectedCallback();
  }

  private stopGeometryObservation(): void {
    this.geometryObserver?.disconnect();
    this.geometryObserver = null;
    if (this.geometryLayoutFrame != null) cancelAnimationFrame(this.geometryLayoutFrame);
    this.geometryLayoutFrame = null;
  }

  /** Observe the public engine callbacks after their placement frame. */
  private scheduleGeometryLayout(): void {
    if (this.geometryLayoutFrame != null || this.disposed || !this.isConnected) return;
    this.geometryLayoutFrame = requestAnimationFrame(() => {
      this.geometryLayoutFrame = null;
      if (!this.disposed && this.isConnected) this.onGeometryLayout();
    });
  }

  private hasUsableGeometry(): boolean {
    if (!this.isConnected || this.$scrollViewport.getClientRects().length === 0) return false;
    const style = getComputedStyle(this.$scrollViewport);
    let width = parseFloat(style.width), height = parseFloat(style.height);
    if (style.boxSizing === "border-box") {
      width -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight) +
        parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
      height -= parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) +
        parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    }
    // Match ResizeObserver's content box; client dimensions include padding
    // and round away fractional geometry.
    return Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0;
  }

  private rememberGeometryLoss(): void {
    if (this.geometryRecovery) {
      // A second loss can interrupt the two-frame placement observation.
      // Retain its logical anchor, retire that placement, and restart on return.
      if (this.geometryRecovery.started) {
        this.geometryRecovery.started = false;
        this.bindGeneration++;
        if (this.anchorFrame != null) cancelAnimationFrame(this.anchorFrame);
        this.anchorFrame = null;
      }
      return;
    }
    if (this.disposed || !this.bindReady ||
      this.lastValidGeometryRevision !== this.anchorInputRevision) return;
    this.geometryRecovery = {
      anchor: this.lastValidAnchor,
      inputRevision: this.anchorInputRevision,
      started: false,
    };
  }

  private onGeometryLayout(): void {
    if (this.disposed || !this.bindReady || !this.hasUsableGeometry()) return;
    const recovery = this.geometryRecovery;
    if (!recovery) {
      if (this.$scroll.scrollEngine.hasPendingBinds) return;
      this.lastValidAnchor = this.captureAnchor();
      this.lastValidGeometryRevision = this.anchorInputRevision;
      return;
    }
    if (recovery.started || recovery.inputRevision !== this.anchorInputRevision) return;
    recovery.started = true;
    if (this.autoScrollEnabled) {
      this.scrollToBottom();
      this.afterGeometryPlacement(recovery, this.bindGeneration);
    } else if (recovery.anchor) {
      void this.rebind(recovery.anchor, recovery);
    } else {
      this.finishGeometryRecovery(recovery);
    }
  }

  private afterGeometryPlacement(recovery: GeometryRecovery, generation: number): void {
    let attempts = 0;
    let revealedAfterLayout = false;
    const settle = () => {
      this.anchorFrame = null;
      if (this.disposed || this.geometryRecovery !== recovery || generation !== this.bindGeneration ||
        recovery.inputRevision !== this.anchorInputRevision) return;
      if (!this.hasUsableGeometry()) { this.rememberGeometryLoss(); return; }
      if (this.$scroll.scrollEngine.hasPendingBinds) {
        if (++attempts < 120) this.anchorFrame = requestAnimationFrame(settle);
        else this.finishGeometryRecovery(recovery);
        return;
      }
      // The first reveal may have preceded the scroll engine's resize layout.
      // Reveal once against its committed extent before releasing suppression.
      if (this.autoScrollEnabled && !revealedAfterLayout) {
        revealedAfterLayout = true;
        this.scrollToBottom();
        this.anchorFrame = requestAnimationFrame(settle);
        return;
      }
      this.finishGeometryRecovery(recovery);
    };
    this.anchorFrame = requestAnimationFrame(() => {
      this.anchorFrame = null;
      this.anchorFrame = requestAnimationFrame(settle);
    });
  }

  private finishGeometryRecovery(recovery: GeometryRecovery): void {
    if (this.geometryRecovery !== recovery || recovery.inputRevision !== this.anchorInputRevision) return;
    this.geometryRecovery = undefined;
    this.lastScrollTop = this.$scrollViewport.scrollTop;
    this.geometryPlacement = { top: this.lastScrollTop, inputRevision: this.anchorInputRevision };
    this.onGeometryLayout();
  }

  private registerHeight(index: number, height: number): void {
    if (this.disposed) return;
    const msg = this.messages[index];
    if (!msg) return;
    const prev = this.measuredHeights.get(msg.id);
    Tracing.log("chat/registerHeight", {
      index,
      prev: prev ?? null,
      height,
      bindReady: this.bindReady,
      isFirstMeasure: prev === undefined,
    });
    if (!this.bindReady) return;
    if (prev === height) return;

    this.unflushedEndShiftPx += unflushedEndShiftPx({
      index,
      lastIndex: this.messages.length - 1,
      prev: prev ?? null,
      height,
      estimatePx: CONSERVATIVE_CELL_HEIGHT,
    });
    this.measuredHeights.set(msg.id, height);
    this.dirtyMinIndex = Math.min(this.dirtyMinIndex, index);

    if (!this.heightsDirty) {
      this.heightsDirty = true;
      $$.measure(() => this.flushHeights());
    }
  }

  private flushHeights(): void {
    if (!this.heightsDirty) return;
    this.heightsDirty = false;
    this.unflushedEndShiftPx = 0;
    const minIndex = this.dirtyMinIndex;
    this.dirtyMinIndex = Number.MAX_SAFE_INTEGER;

    Tracing.log("chat/flushHeights", {
      minIndex,
      autoScrollEnabled: this.autoScrollEnabled,
      userScrolling: this.userScrolling,
      anchor: !this.autoScrollEnabled,
      messageCount: this.messages.length,
    });

    this.$scroll.invalidateHeight(minIndex, !this.autoScrollEnabled);
    this.scheduleGeometryLayout();

    // Pinned to the bottom means pinned through a height change too, even
    // inside the scroll-settle window our own pin opened (see auto-scroll.ts).
    if (
      repinsAfterHeightFlush({
        autoScrollEnabled: this.autoScrollEnabled,
        userScrolling: this.userScrolling,
      })
    ) {
      this.scrollToBottom();
    }
  }

  private scrollToBottom(): void {
    if (this.messages.length === 0) return;
    if (!this.bindReady) return;
    if (!this.hasUsableGeometry()) return;
    const lastIndex = this.messages.length - 1;
    // Move to the tail and nothing else: the messages are already bound, and
    // re-origining the map here would zero `offsetBase` mid-conversation.
    // (`mutateEnd: false` went with the old bag — `bindIndex` never read it.)
    this.$scroll.scrollEngine.bindIndex(lastIndex, {
      kind: "reveal",
      at: {
        position: "end",
        scrollBehavior: "instant",
        useNativeScrollTo: true,
      },
      viewportChangeSource: "bind",
    });
  }

  private captureAnchor(): ThreadAnchor | undefined {
    if (!this.bindReady || this.autoScrollEnabled) return;
    if (this.geometryRecovery) return this.geometryRecovery.anchor;
    if (!this.hasUsableGeometry()) return this.lastValidGeometryRevision === this.anchorInputRevision ? this.lastValidAnchor : undefined;
    const index = this.getVisibleStartIndex();
    if (index == null) return;
    const message = this.messages[index];
    const entry = this.$scroll.scrollEngine.entries.get(index);
    if (!message || !entry) return;
    this.observeAnchorInput();
    return { id: message.id, index,
      offset: entry.t.getBoundingClientRect().top - this.$scrollViewport.getBoundingClientRect().top };
  }

  private observeAnchorInput(): void {
    if (this.anchorInputController) return;
    const controller = this.anchorInputController = new AbortController();
    const cancel = () => {
      this.anchorInputRevision++;
      this.geometryRecovery = undefined;
      this.geometryPlacement = undefined;
      this.lastValidAnchor = undefined;
      if (this.anchorFrame != null) cancelAnimationFrame(this.anchorFrame);
      this.anchorFrame = null;
      // A click or modifier key may never emit another layout/scroll event.
      // Keep its current placement under the new revision, after retiring the
      // older recovery. A subsequent actual scroll records its newer position.
      if (!this.disposed && this.bindReady && this.hasUsableGeometry()) {
        this.lastValidAnchor = this.captureAnchor();
        this.lastValidGeometryRevision = this.anchorInputRevision;
      }
    };
    // Scroll events also come from our own placement correction. Observe input
    // instead so a reader's new intent wins while an old bind is awaiting layout.
    for (const type of ["wheel", "touchstart", "pointerdown", "keydown"]) {
      this.$scrollViewport.addEventListener(type, cancel,
        { capture: true, passive: true, signal: controller.signal });
    }
  }

  private async rebind(anchor?: ThreadAnchor, recovery?: GeometryRecovery): Promise<void> {
    if (this.disposed) return;
    if (this.geometryRecovery && recovery !== this.geometryRecovery) this.geometryRecovery.started = false;
    const generation = ++this.bindGeneration;
    const inputRevision = this.anchorInputRevision;
    const maxIndex = Math.max(0, this.messages.length - 1);
    await this.$scroll.bind({
      cellHeightPx: CONSERVATIVE_CELL_HEIGHT,
      initialIndex: anchor ? this.anchorIndex(anchor) : maxIndex,
      maxIndex,
      minIndex: 0,
      // Every rebind here follows a content change (setMessages, setTheme's
      // drain) — never a pure geometry change — so the rendered range must
      // re-bind even when the shape of the list did not move.
      contextDirtied: true,
    });
    if (this.disposed || generation !== this.bindGeneration) return;
    this.bindReady = true;
    this.scheduleGeometryLayout();
    if (this.autoScrollEnabled) this.scrollToBottom();
    else if (anchor) this.restoreAnchorAfterPlacement(generation, anchor, inputRevision,
      recovery ? () => this.finishGeometryRecovery(recovery) : undefined);
  }

  private anchorIndex(anchor: { id: string; index: number }): number {
    const found = this.messages.findIndex(message => message.id === anchor.id);
    return found >= 0 ? found : Math.min(anchor.index, Math.max(0, this.messages.length - 1));
  }

  private restoreAnchorAfterPlacement(generation: number, anchor: ThreadAnchor, inputRevision: number, settled?: () => void): void {
    if (this.anchorFrame != null) cancelAnimationFrame(this.anchorFrame);
    let attempts = 0;
    const observe = () => {
      this.anchorFrame = null;
      if (this.disposed || generation !== this.bindGeneration ||
        inputRevision !== this.anchorInputRevision || this.autoScrollEnabled) return;
      if (!this.hasUsableGeometry()) {
        this.geometryRecovery ??= { anchor, inputRevision, started: false };
        this.rememberGeometryLoss();
        return;
      }
      if (this.$scroll.scrollEngine.hasPendingBinds) {
        if (++attempts < 120) this.anchorFrame = requestAnimationFrame(observe);
        else settled?.();
        return;
      }
      const entry = this.$scroll.scrollEngine.entries.get(this.anchorIndex(anchor));
      if (entry) {
        const offset = entry.t.getBoundingClientRect().top - this.$scrollViewport.getBoundingClientRect().top;
        this.$scroll.listScrollTop += offset - anchor.offset;
      }
      settled?.();
    };
    // A second frame observes committed placement, after the engine's queued
    // measure/mutate pass. A budget tail keeps the same generation pending.
    this.anchorFrame = requestAnimationFrame(() => {
      this.anchorFrame = null;
      if (!this.disposed && generation === this.bindGeneration && inputRevision === this.anchorInputRevision) {
        this.anchorFrame = requestAnimationFrame(observe);
      }
    });
  }

  async addMessage(message: ChatMessage): Promise<void> {
    if (this.disposed) return;
    this.messages.push(message);
    this.enteringId = message.id;
    if (!this.bindReady) {
      await this.rebind();
    } else {
      this.$scroll.updateMaxIndex(this.messages.length - 1);
      this.scheduleGeometryLayout();
    }
    if (this.autoScrollEnabled) {
      this.scrollToBottom();
    }
  }

  refreshBoundaries(): void {
    if (this.disposed) return;
    for (const [, bubble] of this.$scroll.scrollEngine.entries._all) {
      bubble.toggleAttribute("debug-boundaries", this.showBoundaries);
    }
  }

  updateMessageText(index: number, text: string): void {
    if (this.disposed) return;
    const msg = this.messages[index];
    if (!msg) return;
    if (msg.content.length > 0 && msg.content[0].type === "text") {
      msg.content[0].text = text;
    }
    const entry = this.$scroll.scrollEngine.entries.get(index);
    if (entry) {
      entry.t.bind(msg, this.pluginManager, index);
    }
  }

  setCellHeight(index: number, height: number): void {
    if (this.disposed) return;
    const msg = this.messages[index];
    if (!msg) return;
    this.measuredHeights.set(msg.id, height);
    this.$scroll.invalidateHeight(index, true);
    this.scheduleGeometryLayout();
  }

  getVisibleStartIndex(): Option<number> {
    return this.$scroll.scrollEngine.viewport.visible.indexRange.start ?? null;
  }

  async setMessages(messages: ChatMessage[]): Promise<void> {
    if (this.disposed) return;
    const anchor = this.captureAnchor();
    const sameOrder = this.bindReady && messages.length === this.messages.length &&
      messages.every((message, index) => message.id === this.messages[index].id);
    this.messages = messages;
    if (sameOrder && !this.geometryRecovery && this.hasUsableGeometry()) {
      // Content refresh preserves the engine's origin and the reader's offset.
      this.$scroll.scrollEngine.bindIndex(this.getVisibleStartIndex() ?? 0,
        { kind: "refresh", viewportChangeSource: "bind" });
      if (this.autoScrollEnabled) this.scrollToBottom();
    } else await this.rebind(anchor);
  }

  interruptMessage(messageId: string): void {
    if (this.disposed) return;
    const msgIndex = this.messages.findIndex((m) => m.id === messageId);
    if (msgIndex < 0) return;
    const message = this.messages[msgIndex];
    if (!message.isStreaming) return;

    if (message.content.length === 1 && message.content[0].type === "loading") {
      message.content = [{ type: "interrupted" }];
    } else {
      const lastIndex = message.content.length - 1;
      const entry = this.$scroll.scrollEngine.entries.get(msgIndex);
      if (entry) {
        entry.t.finalizeBlock(lastIndex);
      }
      message.content.push({ type: "interrupted" });
    }

    message.isStreaming = false;
    message.streamEndedAt = DateTime.utc.now();

    if (this.flushToolBlocks(messageId, false)) this.pendingToolBlocks.delete(messageId);

    const entry = this.$scroll.scrollEngine.entries.get(msgIndex);
    if (entry) {
      entry.t.bind(message, this.pluginManager, msgIndex);
    }
  }

  appendToolBlock(messageId: string, block: ChatContentBlock): void {
    if (this.disposed) return;
    let pending = this.pendingToolBlocks.get(messageId);
    if (!pending) {
      pending = [];
      this.pendingToolBlocks.set(messageId, pending);
    }
    pending.push(block);
    this.flushToolBlocks(messageId, true);
  }

  /** Host notes and transient decorations occupy a temporary tail, outside
   * provider indexes. Keep note identities queued until a successful stop so
   * a later provider start can reclaim that tail without losing the note.
   * Interior decorations remain index-bearing quiet slots instead. */
  private providerContent(messageId: string, content: ChatContentBlock[]): ChatContentBlock[] {
    const notes = new Set(this.pendingToolBlocks.get(messageId));
    let end = content.length;
    while (end > 0) {
      const block = contentBlockAt(content[end - 1], end - 1);
      if (!notes.has(content[end - 1]) && !this.transientBlockTypes.has(block.type) &&
          block.type !== "quiet_gap") break;
      end--;
    }
    return end === content.length ? content : content.slice(0, end);
  }

  private flushToolBlocks(messageId: string, rebind: boolean): boolean {
    const pending = this.pendingToolBlocks.get(messageId);
    if (!pending || pending.length === 0) return true;
    const msgIndex = this.messages.findIndex((m) => m.id === messageId);
    if (msgIndex < 0) return false;
    const message = this.messages[msgIndex];
    if (message.content.length === 0) return false;
    if (message.content.some((b, index) => contentBlockAt(b, index).type === "loading")) return false;

    const content = preserveBlockSlots(this.providerContent(messageId, message.content), this.transientBlockTypes);
    const present = new Set(content);
    for (const tb of pending) {
      if (present.has(tb)) continue;
      content.push(tb);
      present.add(tb);
    }
    // Prepare off-model and assign once: a failed normalization or assignment
    // leaves the pending identities available for the next attempt.
    message.content = content;
    if (rebind) {
      const entry = this.$scroll.scrollEngine.entries.get(msgIndex);
      if (entry) entry.t.bind(message, this.pluginManager, msgIndex);
    }
    return true;
  }

  applyDelta(delta: StreamDelta): void {
    if (this.disposed) return;
    if (delta.type === "pipeline_debug") return;
    const msgIndex = this.messages.findIndex((m) => m.id === delta.messageId);
    if (msgIndex < 0) return;
    const message = this.messages[msgIndex];

    switch (delta.type) {
      case "content_block_start": {
        if (!Number.isInteger(delta.blockIndex) || delta.blockIndex < 0 ||
            delta.blockIndex >= MAX_STREAM_BLOCKS || message.content.length > MAX_STREAM_BLOCKS) {
          Tracing.err("ui")("chat/invalid-block-index", { index: delta.blockIndex });
          return;
        }
        const content = message.content.length === 1 && message.content[0]?.type === "loading"
          ? [] : this.providerContent(delta.messageId, message.content);
        message.content = preserveBlockSlots(content, this.transientBlockTypes, delta.blockIndex + 1);
        message.content[delta.blockIndex] = contentBlockAt(delta.block, delta.blockIndex);
        this.flushToolBlocks(delta.messageId, false);
        const entry = this.$scroll.scrollEngine.entries.get(msgIndex);
        if (entry) {
          entry.t.bind(message, this.pluginManager, msgIndex);
        }
        break;
      }
      case "text_delta": {
        // The model is the sole source of truth for streamed content: an
        // off-screen block never sees the plugin's appendDelta, and a
        // rebind (recycle, message_stop) reconstructs from the model — so
        // every streamable shape canonicalizes here, BEFORE the mounted
        // renderer is notified. Plugins paint; they do not own state.
        const block = message.content[delta.blockIndex];
        if (!block) return;
        if (!this.blockReducers.appendDelta(block, delta.delta)) {
          Tracing.err("ui")("chat/unsupported-delta-kind", { type: block.type });
          return; // No mount may paint content absent from canonical data.
        }
        const entry = this.$scroll.scrollEngine.entries.get(msgIndex);
        if (entry) {
          entry.t.appendDelta(delta.blockIndex, delta.delta);
        }
        break;
      }
      case "content_block_stop": {
        const entry = this.$scroll.scrollEngine.entries.get(msgIndex);
        if (entry) {
          entry.t.finalizeBlock(delta.blockIndex);
        }
        break;
      }
      case "message_stop": {
        message.isStreaming = false;
        message.streamEndedAt = DateTime.utc.now();
        if (this.flushToolBlocks(delta.messageId, false)) this.pendingToolBlocks.delete(delta.messageId);
        const entry = this.$scroll.scrollEngine.entries.get(msgIndex);
        if (entry) {
          entry.t.bind(message, this.pluginManager, msgIndex);
        }
        break;
      }
    }
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        display: block;
        width: 100%;
        height: 100%;
        overflow: hidden;
        position: relative;
      }

      /* The thread's safe area, from two owners summed: the LOOK's
         aesthetic breathing room (--thread-*-inset, stated by theme
         sheets) and the HOST's floating chrome (--thread-*-occlusion — a
         composer overlaid on the thread declares its height here). Both
         land as scroll-viewport padding, so rows scroll THROUGH the zone
         rather than clipping at a frame; neither owner clobbers the
         other's var. */
      #scroll {
        /* A custom element is inline until somebody says otherwise. Width
           and height percentages do not size an inline box, so the scroll
           host used to shrink to its contents and every virtualized turn's
           own 100% width merely filled that narrower box. The thread owns
           the viewport geometry: make it a block, then each constituent turn
           stretches to the thread's current width (including after resize). */
        display: block;
        width: 100%;
        height: 100%;
        --scroll-padding-top: calc(
          var(--thread-top-inset, 0px) + var(--thread-top-occlusion, 0px)
        );
        --scroll-padding-bottom: calc(
          var(--thread-bottom-inset, 0px) +
          var(--thread-bottom-occlusion, 0px)
        );
      }

    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-scroll-vertical id="scroll"></std-scroll-vertical>
  `;
}

export namespace ChatThread {
  export type Props = {
    messages?: ChatMessage[];
  };
}
